import {
  RAW_MATERIAL_KIND_LABELS,
  type StockBucket,
} from "@artisancode/api-types";
import { History, Package } from "lucide-react";
import { useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useStockCard } from "@/hooks/use-stock-card";

import { StockCardEntries } from "./stock-card-entries";

import type { StockItem } from "./types";

const BUCKET_FILTERS: { value: StockBucket | "all"; label: string }[] = [
  { value: "all", label: "Semua Bucket" },
  { value: "on_hand", label: "Di Toko (Utuh)" },
  { value: "repacking", label: "Di Bar (Terbuka)" },
  { value: "rider", label: "Di Rider" },
];

interface StockCardDialogProps {
  item: StockItem | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function StockCardDialog({
  item,
  open,
  onOpenChange,
}: StockCardDialogProps) {
  const [selectedBucket, setSelectedBucket] = useState<string>("all");

  const { data: stockCardRes, isLoading } = useStockCard(
    item?.type || "",
    item?.itemId || "",
    {
      bucket:
        selectedBucket !== "all" ? (selectedBucket as StockBucket) : undefined,
    },
  );

  if (!item) return null;

  const entries = stockCardRes?.entries || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-4xl max-h-[85vh] flex flex-col p-4 sm:p-6">
        <DialogHeader>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4 pr-6 sm:pr-0">
            <div className="flex items-start sm:items-center gap-2">
              <History className="w-5 h-5 text-primary shrink-0 mt-0.5 sm:mt-0" />
              <div>
                <DialogTitle className="text-base sm:text-xl font-bold flex flex-wrap items-center gap-2">
                  <span>Kartu Stok: {item.name}</span>
                  <Badge variant="outline" className="text-xs">
                    {item.type === "raw_material"
                      ? item.kind
                        ? RAW_MATERIAL_KIND_LABELS[item.kind]
                        : "Bahan Baku"
                      : "Produk Siap Jual"}
                  </Badge>
                </DialogTitle>
                <DialogDescription className="text-xs">
                  {item.sku ? `SKU: ${item.sku} • ` : ""}
                  Satuan: {item.uomName}
                </DialogDescription>
              </div>
            </div>

            <div className="flex items-center gap-4 text-left sm:text-right">
              <div className="bg-muted/50 px-3 py-1.5 rounded-lg border w-full sm:w-auto">
                <div className="text-xs text-muted-foreground">
                  Saldo Saat Ini
                </div>
                <div className="text-base sm:text-lg font-bold text-foreground">
                  {item.currentStock.toLocaleString("id-ID")} {item.uomName}
                </div>
              </div>
            </div>
          </div>
        </DialogHeader>

        {/* Bucket Filter */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b pb-3 pt-1">
          <div className="flex items-center rounded-lg border bg-muted/30 p-1 overflow-x-auto max-w-full">
            {BUCKET_FILTERS.map((f) => (
              <Button
                key={f.value}
                type="button"
                variant={selectedBucket === f.value ? "secondary" : "ghost"}
                size="sm"
                onClick={() => setSelectedBucket(f.value)}
                className="h-8 shrink-0 text-xs font-medium sm:h-7"
              >
                {f.label}
              </Button>
            ))}
          </div>

          <div className="text-xs text-muted-foreground whitespace-nowrap pl-1 sm:pl-4">
            Total {entries.length} mutasi
          </div>
        </div>

        {/* Content Table */}
        <div className="flex-1 overflow-auto -mx-4 sm:-mx-6 px-4 sm:px-6">
          {isLoading ? (
            <div className="py-16 text-center text-sm text-muted-foreground">
              Memuat data kartu stok...
            </div>
          ) : entries.length === 0 ? (
            <div className="py-16 text-center">
              <Package className="w-10 h-10 text-muted-foreground/40 mx-auto mb-2" />
              <p className="font-medium text-muted-foreground">
                Belum ada riwayat mutasi untuk barang ini
              </p>
              <p className="text-xs text-muted-foreground/70 mt-1">
                Mutasi stok akan otomatis tercatat saat belanja, opname,
                produksi, atau transaksi
              </p>
            </div>
          ) : (
            <StockCardEntries entries={entries} uomName={item.uomName} />
          )}
        </div>

        <div className="border-t pt-3 flex justify-end">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="min-h-[40px] w-full sm:w-auto"
          >
            Tutup
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
